import type {
  DateRangeValue,
  FilterFieldDefinition,
  FilterItem,
} from "@components/TableFilters/FiltersPanel/FiltersPanel.types";
import type {
  BerthVisitedCode,
  DeficiencyCompany,
  DeficiencySearchRequest,
  DeficiencyStatus,
} from "@services/deficiencies/types";
import { convertUaeToUtc, toDate } from "@utils/date-time";

export const DEFICIENCIES_FIELDS = {
  id: "refOrderNumber",
  imoNumber: "imoNumber",
  vesselName: "vesselName",
  shippingAgency: "shippingAgency",
  tripNoPlda: "tripNoPlda",
  berthVisited: "berthVisited",
  loa: "loa",
  deficiencyDetails: "deficiencyDetails",
  deficiencyDateTime: "deficiencyDateTime",
  submittedBy: "submittedBy",
  resolutionCreatedAt: "resolutionCreatedAt",
  approvedRejectedAt: "approvedRejectedAt",
  approvedRejectedBy: "approvedRejectedBy",
  status: "status",
} as const;

const DEFICIENCY_STATUSES = ["PENDING_RESOLUTION", "AWAITING_APPROVAL", "RESOLVED"] as const;
type DeficiencyBadgeType = "success" | "warning";

export const truncateText = (value?: string, notApplicable: string = "N/A"): string => {
  const text = !value ? notApplicable : value;

  return text.length <= 16 ? text : `${text.slice(0, 13)}...`;
};

export const formatDeficiencyStatus = (status?: DeficiencyStatus | null) => {
  if (!status) return "-";

  return status
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));
};

export const getDeficiencyBadgeType = (status?: DeficiencyStatus | null): DeficiencyBadgeType => {
  if (status === "RESOLVED") {
    return "success";
  }

  return "warning";
};

export const formatBerthVisited = (value?: string | null) => {
  if (!value) return "-";

  const normalizedValue = value.trim();
  if (/^B\d{2}$/i.test(normalizedValue)) {
    return `Berth ${normalizedValue.slice(1)}`;
  }

  return normalizedValue;
};

export const formatLoa = (value?: number | string | null) => {
  if (value == null || value === "") return "-";
  if (typeof value === "string") return value;
  return `${value} m`;
};

const LANG_BASE = "deficiencies.headerLabels";
const DEFICIENCY_TEXT_FILTER_MIN_LENGTH = 3;

export const buildDeficienciesFilterFields = (
  t: (key: string) => string,
  companies: DeficiencyCompany[] = [],
  berthVisitedCodes: BerthVisitedCode[] = [],
): Record<string, FilterFieldDefinition> => ({
  refOrderNumber: {
    label: t(`${LANG_BASE}.id`),
    type: "text",
    minLength: 1,
  },
  imoNumber: {
    label: t(`${LANG_BASE}.imoNumber`),
    type: "text",
    minLength: DEFICIENCY_TEXT_FILTER_MIN_LENGTH,
  },
  vesselName: {
    label: t(`${LANG_BASE}.vesselName`),
    type: "text",
    minLength: DEFICIENCY_TEXT_FILTER_MIN_LENGTH,
  },
  shippingAgency: {
    label: t(`${LANG_BASE}.shippingAgency`),
    type: "autocomplete",
    options: companies
      .filter(
        ({ companyCode, companyNameEnglish }) => companyCode?.trim() && companyNameEnglish?.trim(),
      )
      .sort((a, b) => a.companyNameEnglish.trim().localeCompare(b.companyNameEnglish.trim()))
      .map(({ companyCode, companyNameEnglish }) => {
        const companyName = companyNameEnglish.trim();

        return {
          label: companyName,
          value: companyCode.trim(),
          key: companyCode.trim(),
        };
      }),
    inputLengthForDropdown: 1,
    matchFrom: "start",
  },
  tripNoPlda: {
    label: t(`${LANG_BASE}.tripNoPlda`),
    type: "text",
    minLength: DEFICIENCY_TEXT_FILTER_MIN_LENGTH,
  },
  berthVisited: {
    label: t(`${LANG_BASE}.berthVisited`),
    type: "autocomplete",
    options: berthVisitedCodes
      .filter(({ berthCode, berthName }) => berthCode?.trim() && berthName?.trim())
      .sort((a, b) => a.berthName.trim().localeCompare(b.berthName.trim()))
      .map(({ berthCode, berthName }) => ({
        label: berthName.trim(),
        value: berthCode.trim(),
        key: berthCode.trim(),
      })),
    inputLengthForDropdown: 1,
    matchFrom: "start",
  },
  loa: {
    label: t(`${LANG_BASE}.loa`),
    type: "text",
    minLength: DEFICIENCY_TEXT_FILTER_MIN_LENGTH,
  },
  details: {
    label: t(`${LANG_BASE}.deficiencyDetails`),
    type: "text",
    minLength: DEFICIENCY_TEXT_FILTER_MIN_LENGTH,
  },
  createdAt: {
    label: t(`${LANG_BASE}.deficiencyDateTime`),
    type: "dateFromTo",
  },
  submittedBy: {
    label: t(`${LANG_BASE}.submittedBy`),
    type: "text",
    minLength: DEFICIENCY_TEXT_FILTER_MIN_LENGTH,
  },
  resolutionCreatedAt: {
    label: t(`${LANG_BASE}.resolutionCreatedAt`),
    type: "dateFromTo",
  },
  approvedRejectedAt: {
    label: t(`${LANG_BASE}.approvedRejectedAt`),
    type: "dateFromTo",
  },
  approvedRejectedBy: {
    label: t(`${LANG_BASE}.approvedRejectedBy`),
    type: "text",
    minLength: DEFICIENCY_TEXT_FILTER_MIN_LENGTH,
  },
  status: {
    label: t(`${LANG_BASE}.status`),
    inputLengthForDropdown: 1,
    type: "autocomplete",
    matchFrom: "start",
    options: DEFICIENCY_STATUSES.map((status) => ({
      label: t(`deficiencies.status-labels.${status}`),
      value: status,
    })),
  },
});

export const buildDeficienciesFilterBody = (filters: FilterItem[]): DeficiencySearchRequest => {
  const body: DeficiencySearchRequest = {};

  for (const filter of filters) {
    if (!filter.field || filter.value == null || filter.value === "") {
      continue;
    }

    switch (filter.field) {
      case "refOrderNumber": {
        const parsed = Number(filter.value);
        if (!Number.isNaN(parsed)) {
          body.refOrderNumber = [...(body.refOrderNumber ?? []), parsed];
        }
        break;
      }

      case "imoNumber": {
        const parsed = Number(filter.value);
        if (!Number.isNaN(parsed)) {
          body.imoNumber = [...(body.imoNumber ?? []), parsed];
        }
        break;
      }

      case "vesselName": {
        const value = String(filter.value).trim();
        body.vesselName = [...(body.vesselName ?? []), value];
        break;
      }

      case "shippingAgency": {
        const value = String(filter.value).trim();
        body.companyCode = [...(body.companyCode ?? []), value];
        break;
      }

      case "tripNoPlda": {
        const value = String(filter.value).trim();
        body.tripNoPlda = [...(body.tripNoPlda ?? []), value];
        break;
      }
      case "berthVisited": {
        const value = String(filter.value).trim();
        body.berthVisitedCode = [...(body.berthVisitedCode ?? []), value];
        break;
      }

      case "loa": {
        const parsed = Number(filter.value);
        if (!Number.isNaN(parsed)) {
          body.loa = [...(body.loa ?? []), parsed];
        }
        break;
      }

      case "details": {
        const value = String(filter.value).trim();
        body.details = [...(body.details ?? []), value];
        break;
      }
      case "submittedBy": {
        const value = String(filter.value).trim();
        body.createdBy = [...(body.createdBy ?? []), value];

        break;
      }
      case "approvedRejectedBy": {
        const value = String(filter.value).trim();
        body.approvedRejectedBy = [...(body.approvedRejectedBy ?? []), value];

        break;
      }

      case "status": {
        const value = String(filter.value)
          .trim()
          .replace(/[\s-]+/g, "_")
          .toUpperCase() as DeficiencyStatus;
        body.status = [...(body.status ?? []), value];
        break;
      }

      case "createdAt": {
        const range = filter.value as DateRangeValue;
        const fromDate = toDate(range.from);
        const toDateValue = toDate(range.to);

        if (fromDate || toDateValue) {
          body.createdAt = [
            ...(body.createdAt ?? []),
            {
              ...(fromDate ? { from: convertUaeToUtc(fromDate) } : {}),
              ...(toDateValue ? { to: convertUaeToUtc(toDateValue) } : {}),
            },
          ];
        }
        break;
      }

      case "resolutionCreatedAt": {
        const range = filter.value as DateRangeValue;
        const fromDate = toDate(range.from);
        const toDateValue = toDate(range.to);

        if (fromDate || toDateValue) {
          body.resolutionCreatedAt = [
            ...(body.resolutionCreatedAt ?? []),
            {
              ...(fromDate ? { from: convertUaeToUtc(fromDate) } : {}),
              ...(toDateValue ? { to: convertUaeToUtc(toDateValue) } : {}),
            },
          ];
        }
        break;
      }

      case "approvedRejectedAt": {
        const range = filter.value as DateRangeValue;
        const fromDate = toDate(range.from);
        const toDateValue = toDate(range.to);

        if (fromDate || toDateValue) {
          body.approvedRejectedAt = [
            ...(body.approvedRejectedAt ?? []),
            {
              ...(fromDate ? { from: convertUaeToUtc(fromDate) } : {}),
              ...(toDateValue ? { to: convertUaeToUtc(toDateValue) } : {}),
            },
          ];
        }
        break;
      }

      default:
        break;
    }
  }

  return body;
};
